/**
 * Theme deep dive — performance since identification, member roles,
 * and per-stock deep research (Parallel.ai) with citations.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, FlaskConical, Loader2, Sparkles, Users,
} from "lucide-react";
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { API } from "../components/stats/statsApi";

const pct = (v, dp = 1) => (v == null ? "—" : `${(v * 100).toFixed(dp)}%`);
const cap = (v) => {
  if (!v) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
};
const STAGE_STYLE = {
  Accumulating: "text-emerald-400 border-emerald-500/40",
  Igniting: "text-amber-400 border-amber-500/40",
  Mixed: "text-muted-foreground border-border",
  Distributing: "text-red-400 border-red-500/40",
};

const Stat = ({ label, value, tone }) => (
  <div className="px-3 py-2 rounded bg-secondary/40">
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`font-mono text-sm font-bold ${tone || ""}`}>{value}</div>
  </div>
);

const PerfChart = ({ perf }) => {
  if (!perf?.series?.length) return null;
  const s = perf.stats || {};
  return (
    <Card data-testid="theme-perf-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between flex-wrap gap-2">
          <span>Performance vs S&amp;P 500 — identified {perf.born}</span>
          <div className="flex gap-2">
            <Stat label="Since identification" value={pct(s.live_total)}
                  tone={s.live_total >= 0 ? "text-emerald-400" : "text-red-400"} />
            <Stat label="SPY since id." value={pct(s.spy_total)} />
            <Stat label="Excess since id." value={pct(s.excess)}
                  tone={(s.excess ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"} />
            <Stat label="6M before id." value={pct(s.pre_6m)} />
            <Stat label="6M excess vs SPY" value={pct(s.pre_excess)}
                  tone={(s.pre_excess ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"} />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={perf.series} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={60} />
            <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
            <Tooltip contentStyle={{ background: "#111827", border: "1px solid #374151", fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine x={perf.anchor} stroke="#f59e0b" strokeDasharray="3 3"
                           label={{ value: "identified", fontSize: 10, fill: "#f59e0b", position: "insideTopRight" }} />
            <Line type="monotone" dataKey="pre" name="basket (pre-identification)"
                  stroke="#6b7280" strokeDasharray="4 3" dot={false} strokeWidth={1.4} />
            <Line type="monotone" dataKey="live" name="theme basket (live)"
                  stroke="#f59e0b" dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="spy" name="S&P 500"
                  stroke="#94a3b8" dot={false} strokeWidth={1.2} />
          </LineChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-muted-foreground mt-1">
          Equal-weight members, rebalanced at each quarterly run. All series rebased 100 at
          window start — the visual gap vs S&amp;P 500 IS the out/underperformance. Dashed = same
          basket BEFORE identification (context); amber marker = identification date;
          {" "}{s.days_live ?? 0} trading days live.
        </p>
      </CardContent>
    </Card>
  );
};

const ResearchCard = ({ m, r }) => (
  <Card data-testid={`research-card-${m.symbol}`}>
    <CardContent className="p-4 space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="font-bold">{m.symbol}</span>
          <span className="text-muted-foreground text-sm ml-2">{m.name}</span>
        </div>
        <span className="text-xs text-amber-200/80">{m.role}</span>
      </div>
      {r ? (
        <div className="space-y-2 text-xs leading-relaxed">
          {r.narrative ? (
            <>
              {r.narrative.split(/\n\n+/).map((p, i) => (
                <p key={i} className="text-[13px] text-foreground/90">{p}</p>
              ))}
              <details className="pt-1">
                <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
                  Research basis (raw facts)
                </summary>
                <div className="space-y-1.5 mt-2 text-muted-foreground">
                  <p><span className="font-semibold text-amber-300/90">Theme fit — </span>{r.content?.theme_fit}</p>
                  <p><span className="font-semibold text-sky-300/90">Links — </span>{r.content?.intra_theme_links}</p>
                  <p><span className="font-semibold text-emerald-300/90">Catalysts — </span>{r.content?.catalysts}</p>
                  <p><span className="font-semibold text-red-300/80">Risks — </span>{r.content?.risks}</p>
                </div>
              </details>
            </>
          ) : (
            <>
              <p><span className="font-semibold text-amber-300/90">Theme fit — </span>
                {r.content?.theme_fit}</p>
              <p><span className="font-semibold text-sky-300/90">Links inside the theme — </span>
                {r.content?.intra_theme_links}</p>
              <p><span className="font-semibold text-emerald-300/90">Catalysts — </span>
                {r.content?.catalysts}</p>
              <p><span className="font-semibold text-red-300/80">Risks — </span>
                {r.content?.risks}</p>
            </>
          )}
          {r.citations?.length > 0 && (
            <div className="flex gap-2 flex-wrap pt-1">
              {r.citations.slice(0, 6).map((c, i) => (
                <a key={i} href={c.url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground border border-border rounded px-1.5 py-0.5 max-w-[240px] truncate">
                  <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                  {c.title || new URL(c.url).hostname}
                </a>
              ))}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground font-mono">
            researched {r.researched_at?.slice(0, 10)} · parallel.ai {r.processor}
            {r.polished_at ? " · narrative by Opus" : ""}
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No deep research yet for this member.</p>
      )}
    </CardContent>
  </Card>
);

const CohortCard = ({ cohort }) => {
  if (!cohort || !(cohort.core || []).length) return null;
  return (
    <Card data-testid="theme-cohort-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <Users className="w-4 h-4 text-violet-400" /> The cohort behind this trade
          <span className="text-xs text-muted-foreground font-normal">
            {cohort.n_core} of {cohort.n_funds} specialist buyers hold 2+ members — the co-builders
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="flex gap-1 flex-wrap" data-testid="cohort-focus-mix">
          {Object.entries(cohort.focus_mix || {}).sort((a, b) => b[1] - a[1]).map(([sec, n]) => (
            <span key={sec}
                  className="px-1.5 py-0.5 rounded border border-violet-500/30 text-violet-300/90 text-[10px] font-mono">
              {n} {sec} specialist{n > 1 ? "s" : ""}
            </span>
          ))}
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border text-muted-foreground">
            <tr>
              <th className="py-1 px-2 text-left">Manager</th>
              <th className="py-1 px-2 text-left">Specialization</th>
              <th className="py-1 px-2 text-right"># names</th>
              <th className="py-1 px-2 text-left">Building</th>
            </tr>
          </thead>
          <tbody>
            {cohort.core.map((f) => (
              <tr key={f.name} className="border-b border-border/40" data-testid={`cohort-fund-${f.name}`}>
                <td className="py-1 px-2 font-medium">{f.name}</td>
                <td className="py-1 px-2 text-muted-foreground">{f.focus || "—"}</td>
                <td className="py-1 px-2 text-right font-mono">{f.n_names}</td>
                <td className="py-1 px-2 font-mono text-muted-foreground">{(f.symbols || []).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(cohort.links || []).length > 0 && (
          <div className="text-[11px] text-muted-foreground space-y-0.5" data-testid="cohort-links">
            {cohort.links.map((l) => (
              <p key={l.theme_id || l.idx}>
                <span className="text-violet-300/90 font-semibold">{l.shared} of these managers</span>
                {" "}are also building{" "}
                {l.theme_id
                  ? <Link to={`/thematic/${l.theme_id}`} className="underline hover:text-foreground">
                      {l.theme || "another theme"}
                    </Link>
                  : (l.theme || "another theme")}
                {" "}({(l.names || []).join(", ")})
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


export default function ThemeDeepPage() {
  const { themeId } = useParams();
  const { token } = useAuth();
  const [detail, setDetail] = useState(null);
  const [perf, setPerf] = useState(null);
  const [research, setResearch] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const poll = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadResearch = useCallback(async () => {
    const res = await fetch(`${API}/themes/${themeId}/research`, auth);
    if (res.ok) {
      const d = await res.json();
      setResearch(d);
      return d;
    }
    return null;
  }, [themeId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    (async () => {
      const res = await fetch(`${API}/themes/${themeId}`, auth);
      if (!res.ok) { setError(`HTTP ${res.status}`); return; }
      setDetail(await res.json());
      fetch(`${API}/themes/${themeId}/performance`, auth)
        .then((r) => (r.ok ? r.json() : null)).then(setPerf);
      loadResearch();
    })();
    return () => clearInterval(poll.current);
  }, [token, themeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setGenerating(true);
    const res = await fetch(`${API}/themes/${themeId}/research/generate`,
                            { method: "POST", ...auth });
    if (!res.ok) {
      setGenerating(false);
      toast.error(res.status === 409 ? "Already running" : "Failed to start");
      return;
    }
    toast.info("Deep research launched — parallel.ai pro (~2-5 min per stock, runs concurrently)");
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const d = await loadResearch();
      if (d && !d.running) {
        clearInterval(poll.current);
        setGenerating(false);
        toast.success("Deep research complete");
      }
    }, 10000);
  };

  const t = detail?.theme;
  const s = t?.scores || {};
  const st = research?.state;

  return (
    <div className="p-6 space-y-5 max-w-[1500px]" data-testid="theme-deep-page">
      <Link to="/thematic" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            data-testid="back-to-themes">
        <ArrowLeft className="w-3.5 h-3.5" /> All themes
      </Link>
      {error && <p className="text-sm text-red-400">{error}</p>}
      {!t && !error && (
        detail?.registry
          ? <p className="text-sm text-muted-foreground">This theme is dormant (not in the latest run).</p>
          : <p className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin mr-1" />Loading…</p>
      )}
      {t && (
        <>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-amber-400" /> {t.name}
                <Badge variant="outline" className={`text-[10px] ${STAGE_STYLE[s.lifecycle] || ""}`}>
                  {s.lifecycle}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-mono">
                  {t.status === "new" || t.status === "split" ? "NEW" : `${t.quarters_alive}Q`}
                </Badge>
                <span className="font-mono text-base">{s.theme_score?.toFixed(2)}</span>
              </h1>
              {t.thesis && <p className="text-sm text-muted-foreground max-w-4xl mt-1">{t.thesis}</p>}
              {t.linkage && (
                <p className="text-xs text-amber-200/70 mt-2 max-w-4xl border-l-2 border-amber-500/30 pl-3">
                  <span className="font-semibold text-amber-300/90">Why one trade: </span>{t.linkage}
                </p>
              )}
              <div className="flex gap-1 flex-wrap mt-2">
                {(t.linkage_tags || []).map((tg) => (
                  <span key={tg} className="px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-300/90 text-[10px] font-mono">
                    {tg}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Stat label="Members" value={t.n_members} />
              <Stat label="Specialists" value={t.n_specialists} />
              <Stat label="Median cap" value={cap(t.median_cap)} />
              <Stat label="Quiet tape" value={pct(s.quiet_pct, 0)} />
              <Stat label="Strong regimes" value={pct(s.strong_pct, 0)} />
              <Stat label="Coherence" value={s.coherence ?? "—"} />
              {s.health != null && (
                <Stat label={`Health · ${s.freshness || ""}`} value={s.health}
                      tone={s.freshness === "fresh" ? "text-emerald-400"
                            : s.freshness === "aging" ? "text-amber-400" : "text-red-400"} />
              )}
            </div>
          </div>

          <PerfChart perf={perf} />

          <Card data-testid="theme-members-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Members</CardTitle></CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border text-muted-foreground">
                  <tr>
                    <th className="py-1.5 px-2 text-left">Symbol</th>
                    <th className="py-1.5 px-2 text-left">Role in theme</th>
                    <th className="py-1.5 px-2 text-right">Mkt cap</th>
                    <th className="py-1.5 px-2 text-left">Regime</th>
                    <th className="py-1.5 px-2 text-left">Tape</th>
                    <th className="py-1.5 px-2 text-right">Kensys upside</th>
                    <th className="py-1.5 px-2 text-right">Torque</th>
                    <th className="py-1.5 px-2 text-left">Specialist buyers</th>
                  </tr>
                </thead>
                <tbody>
                  {t.members.map((m) => (
                    <tr key={m.symbol} className="border-b border-border/40">
                      <td className="py-1.5 px-2 font-semibold" title={m.name}>
                        {m.symbol}
                        {m.crowded && (
                          <span className="ml-1 px-1 py-0.5 rounded border border-red-500/40 text-red-400 text-[9px] font-mono align-middle"
                                title="Top-decile crowding, not in a strong regime (−2.34%/3M vs SPY, t −3.49)"
                                data-testid={`deep-crowded-badge-${m.symbol}`}>CROWDED</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-amber-200/80 max-w-[260px]">{m.role || "—"}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{cap(m.market_cap)}</td>
                      <td className={`py-1.5 px-2 ${m.strong ? "text-emerald-400" : m.regime ? "text-amber-400" : "text-muted-foreground"}`}>
                        {m.regime || "—"}
                      </td>
                      <td className={`py-1.5 px-2 capitalize ${
                        m.tape === "quiet" ? "text-emerald-400" : m.tape === "hot" ? "text-red-400" : "text-muted-foreground"}`}>
                        {m.tape || "—"}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">{m.kensys_upside != null ? pct(m.kensys_upside, 0) : "—"}</td>
                      <td className="py-1.5 px-2 text-right font-mono font-semibold">{m.torque?.toFixed(2)}</td>
                      <td className="py-1.5 px-2 text-muted-foreground max-w-[320px] truncate"
                          title={(m.specialists || []).join(", ")}>
                        {(m.specialists || []).slice(0, 3).join(", ")}{(m.specialists || []).length > 3 ? ` +${m.specialists.length - 3}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {t.bench?.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-2 font-mono" data-testid="deep-bench">
                  Bench — co-bought cluster members outside the tradeable basket (max 7, tape-first
                  selection): {t.bench.join(", ")}
                </p>
              )}
            </CardContent>
          </Card>

          <CohortCard cohort={t.cohort} />

          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-base font-bold flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-sky-400" /> Deep research per member
              <span className="text-xs text-muted-foreground font-normal">
                facts by parallel.ai deep research · narrative by Claude Opus · cited
              </span>
            </h2>
            <div className="flex items-center gap-2">
              {(generating || research?.running) && st && (
                <span className="text-xs font-mono text-muted-foreground">
                  {st.done}/{st.total} done{st.failed?.length ? ` · ${st.failed.length} failed` : ""}
                </span>
              )}
              <Button size="sm" onClick={generate} disabled={generating || research?.running}
                      data-testid="generate-research-btn">
                {generating || research?.running
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Researching…</>
                  : Object.keys(research?.research || {}).length > 0
                    ? "Refresh research" : "Generate deep research"}
              </Button>
            </div>
          </div>
          {research?.intro && (
            <Card className="border-amber-500/20" data-testid="theme-intro-card">
              <CardContent className="p-4">
                <p className="text-[13px] leading-relaxed text-foreground/90 italic">
                  {research.intro}
                </p>
              </CardContent>
            </Card>
          )}
          <div className="grid gap-3 xl:grid-cols-2">
            {t.members.map((m) => (
              <ResearchCard key={m.symbol} m={m} r={research?.research?.[m.symbol]} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
